"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmark, faPhone, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { ThemeToggle } from "./ThemeToggle";
import type { SiteSettings } from "@/types";
import { cn } from "@/lib/utils";

interface HeaderProps {
  settings: SiteSettings | null;
}

export function Header({ settings }: HeaderProps) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const defaultNavigation = [
    { label: "Home", href: "/", order: 0, isVisible: true },
    { label: "About", href: "/about", order: 1, isVisible: true },
    { label: "Services", href: "/services", order: 2, isVisible: true },
    { label: "Contact", href: "/contact", order: 3, isVisible: true },
    { label: "Blog", href: "/blog", order: 4, isVisible: true },
  ];

  const filtered = settings?.navigation?.filter((n) => n.isVisible)
    .sort((a, b) => a.order - b.order);
  const navigation = filtered && filtered.length > 0 ? filtered : defaultNavigation;

  const whatsappNumber = settings?.ctaButtons?.whatsapp?.number || settings?.socialLinks?.whatsapp || "+918592043363";

  return (
    <header className="sticky top-0 z-60 w-full border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2">
          {settings?.logo?.url ? (
            <Image
              src={settings.logo.url}
              alt={settings.siteName || "MotoHome"}
              width={40}
              height={40}
              className="h-10 w-10"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg">
              M
            </div>
          )}
          <span className="text-xl font-bold">
            {settings?.siteName || "MotoHome"}
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navigation.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-md transition-colors hover:bg-accent hover:text-accent-foreground",
                pathname === item.href
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-2">
          {whatsappNumber && (
            <a
              href={`https://wa.me/${whatsappNumber.replace(/\D/g, "")}?text=Hello%20MotoHome,%20I%20would%20like%20to%20inquire%20about%20your%20services.`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-linear-to-br from-green-500 to-green-600 text-white shadow-md transition-all hover:scale-110 hover:shadow-lg"
              aria-label="Chat on WhatsApp"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
            </a>
          )}
          <ThemeToggle />
        </div>

        {/* Mobile Menu */}
        <div className="flex md:hidden items-center gap-2">
          <ThemeToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="inline-flex items-center justify-center rounded-md h-9 w-9 hover:bg-accent hover:text-accent-foreground">
              <FontAwesomeIcon icon={open ? faXmark : faBars} className="h-5 w-5 transition-transform duration-200" />
              <span className="sr-only">Toggle menu</span>
            </SheetTrigger>
            <SheetContent side="right" className="w-full! sm:max-w-full! top-16! h-[calc(100%-4rem)]! items-center pt-10" showCloseButton={false}>
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <nav className="flex flex-col items-center gap-4">
                {navigation.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "px-6 py-3 text-lg font-medium rounded-md transition-colors text-center",
                      pathname === item.href
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="flex flex-col items-center gap-3 mt-6">
                {whatsappNumber && (
                  <a
                    href={`https://wa.me/${whatsappNumber.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(buttonVariants({ size: "lg" }), "bg-green-600 hover:bg-green-700 w-48")}
                  >
                    <FontAwesomeIcon icon={faWhatsapp} className="mr-2 h-4 w-4" />
                    WhatsApp
                  </a>
                )}
                <a
                  href="tel:+918592043363"
                  className={cn(buttonVariants({ variant: "outline", size: "lg" }), "w-48")}
                >
                  <FontAwesomeIcon icon={faPhone} className="mr-2 h-4 w-4" />
                  +91 85920 43363
                </a>
              </div>
              <div className="mt-auto flex flex-col items-center gap-4 pb-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2 text-center">
                  <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4 shrink-0" />
                  Near Mahe Bypass, Chirakunni
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
